<template>
  <div>
  <v-container v-if="isAdministrativeProfile" class="dashboard-page">
    <div class="dashboard-header">
      <div>
        <span class="page-title">Visão Geral</span>
        <p class="dashboard-subtitle">Acompanhamento operacional da escola em tempo real</p>
      </div>
      <div class="dashboard-range">
        {{ formattedRange }}
      </div>
    </div>

    <v-card class="filter-card dashboard-filter">
      <v-card-title class="filter-card-title">Filtros</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="loadData()">
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                :disabled="dataset.loading"
                @change="normalizeRangeByType"
                dense
                :items="dataset.periodTypes"
                label="Período"
                v-model="dataset.filterSelected"
                item-value="type"
                item-text="label"
                outlined
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-dialog ref="dialogStart" v-model="dataset.datePickerStartDialog" persistent width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="$moment(dataset.rangeDate.startDate).format('DD/MM/YYYY')"
                    :disabled="dataset.filterSelected != 9"
                    readonly
                    dense
                    label="Data início"
                    v-bind="attrs"
                    v-on="dataset.filterSelected == 9 ? on : null"
                    outlined
                    hide-details
                  />
                </template>
                <v-date-picker locale="pt-br" v-model="dataset.rangeDate.startDate" scrollable>
                  <v-spacer />
                  <v-btn text color="primary" @click="dataset.datePickerStartDialog = false">Cancelar</v-btn>
                  <v-btn text color="primary" @click="$refs.dialogStart.save(dataset.rangeDate.startDate); dataset.datePickerStartDialog = false">Salvar</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="12" md="4">
              <v-dialog ref="dialogEnd" v-model="dataset.datePickerEndDialog" persistent width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="$moment(dataset.rangeDate.endDate).format('DD/MM/YYYY')"
                    :disabled="dataset.filterSelected != 9"
                    readonly
                    dense
                    label="Data fim"
                    v-bind="attrs"
                    v-on="dataset.filterSelected == 9 ? on : null"
                    outlined
                    hide-details
                  />
                </template>
                <v-date-picker locale="pt-br" v-model="dataset.rangeDate.endDate" scrollable>
                  <v-spacer />
                  <v-btn text color="primary" @click="dataset.datePickerEndDialog = false">Cancelar</v-btn>
                  <v-btn text color="primary" @click="$refs.dialogEnd.save(dataset.rangeDate.endDate); dataset.datePickerEndDialog = false">Salvar</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn :loading="dataset.loading" type="submit" elevation="0" color="primary">Filtrar</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <div v-if="dataset.loading" class="dashboard-kpis">
      <v-card v-for="item in 5" :key="`kpi-skeleton-${item}`" class="dashboard-kpi dashboard-kpi-skeleton">
        <v-skeleton-loader type="avatar" class="kpi-skeleton-icon" />
        <v-skeleton-loader type="list-item-two-line" class="kpi-skeleton-content" />
      </v-card>
    </div>

    <div v-else class="dashboard-kpis">
      <v-card v-for="item in kpiCards" :key="item.key" class="dashboard-kpi">
        <div class="kpi-icon">
          <v-icon>{{ item.icon }}</v-icon>
        </div>
        <div>
          <span class="kpi-label">{{ item.title }}</span>
          <strong>{{ item.value }}</strong>
          <small>{{ item.subtitle }}</small>
        </div>
      </v-card>
    </div>

    <v-row>
      <v-col cols="12" lg="8">
        <v-card class="dashboard-panel">
          <div class="panel-header">
            <div>
              <h2>Acessos por data</h2>
              <p>Entradas registradas no período selecionado</p>
            </div>
            <v-chip v-if="!dataset.loading" label small color="primary" text-color="white">{{ totalAccesses }} acessos</v-chip>
          </div>
          <v-skeleton-loader
            v-if="dataset.loading"
            class="chart-skeleton"
            type="image"
          />
          <apexchart
            v-else
            height="300"
            type="bar"
            :options="dataset.accessChart.chartOptions"
            :series="dataset.accessChart.series"
          />
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card class="dashboard-panel presence-panel">
          <div class="panel-header">
            <div>
              <h2>Presença agora</h2>
              <p>Leitura baseada no último evento do aluno hoje</p>
            </div>
          </div>

          <div v-if="dataset.loading" class="presence-skeleton">
            <v-skeleton-loader type="heading" />
            <v-skeleton-loader type="text" />
            <v-skeleton-loader type="paragraph" />
          </div>
          <template v-else>
            <div class="presence-number">{{ studentsInside }}</div>
            <div class="presence-caption">alunos dentro da escola</div>

            <div class="presence-bars">
              <div>
                <span>Dentro</span>
                <strong>{{ studentsInside }}</strong>
              </div>
              <v-progress-linear :value="presenceRate" height="8" rounded color="primary" background-color="#e7edf5" />
              <div>
                <span>Fora ou sem leitura hoje</span>
                <strong>{{ studentsOutside }}</strong>
              </div>
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="dataset.loading">
      <v-col v-for="item in 3" :key="`panel-skeleton-${item}`" cols="12" md="4">
        <v-card class="dashboard-panel compact-panel">
          <v-skeleton-loader type="list-item-two-line" />
          <v-skeleton-loader type="paragraph" />
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" md="4">
        <v-card class="dashboard-panel compact-panel">
          <div class="panel-header">
            <div>
              <h2>Equipamentos</h2>
              <p>Status do parque instalado</p>
            </div>
          </div>
          <div class="status-row">
            <span>Online</span>
            <strong>{{ equipmentOnline }}</strong>
          </div>
          <div class="status-row">
            <span>Sem comunicação recente</span>
            <strong>{{ equipmentOffline }}</strong>
          </div>
          <v-progress-linear :value="equipmentOnlineRate" height="8" rounded color="success" background-color="#e7edf5" />
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="dashboard-panel compact-panel">
          <div class="panel-header">
            <div>
              <h2>Base escolar</h2>
              <p>Pessoas cadastradas para operação</p>
            </div>
          </div>
          <div class="status-row">
            <span>Alunos</span>
            <strong>{{ getCount('Alunos') }}</strong>
          </div>
          <div class="status-row">
            <span>Matrículas ativas</span>
            <strong>{{ getCount('Matrículas ativas') }}</strong>
          </div>
          <div class="status-row">
            <span>Responsáveis</span>
            <strong>{{ getCount('Responsáveis') }}</strong>
          </div>
          <div class="status-row">
            <span>Colaboradores</span>
            <strong>{{ getCount('Colaboradores') }}</strong>
          </div>
          <div class="status-row">
            <span>Transportes escolares</span>
            <strong>{{ getCount('Transportes escolares') }}</strong>
          </div>
          <div class="status-row">
            <span>Visitantes ativos</span>
            <strong>{{ getCount('Visitantes ativos') }}</strong>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="dashboard-panel compact-panel">
          <div class="panel-header">
            <div>
              <h2>Pontos de atenção</h2>
              <p>Itens que merecem acompanhamento</p>
            </div>
          </div>
          <div class="attention-item" :class="{ good: equipmentOffline === 0 }">
            <v-icon>{{ equipmentOffline === 0 ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
            <span>{{ equipmentOffline === 0 ? 'Equipamentos operando normalmente' : `${equipmentOffline} equipamento(s) sem comunicação recente` }}</span>
          </div>
          <div class="attention-item" :class="{ good: studentsInside > 0 }">
            <v-icon>{{ studentsInside > 0 ? 'mdi-check-circle-outline' : 'mdi-information-outline' }}</v-icon>
            <span>{{ studentsInside > 0 ? 'Há alunos com presença registrada hoje' : 'Nenhum aluno dentro da escola agora' }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <v-container v-else class="dashboard-page">
    <div class="dashboard-header">
      <div>
        <span class="page-title">{{ profileHomeTitle }}</span>
        <p class="dashboard-subtitle">{{ profileHomeSubtitle }}</p>
      </div>
    </div>

    <div class="profile-actions">
      <v-card
        v-for="item in profileActions"
        :key="item.route"
        class="profile-action-card"
        :to="item.route"
      >
        <v-chip v-if="item.badge !== undefined" class="profile-action-badge" small color="primary" text-color="white">
          {{ item.badge }} aguardando
        </v-chip>
        <div class="kpi-icon">
          <v-icon>{{ item.icon }}</v-icon>
        </div>
        <div>
          <strong>{{ item.title }}</strong>
          <span>{{ item.description }}</span>
        </div>
      </v-card>
    </div>
  </v-container>
  </div>
</template>

<script>
import "moment/locale/pt-br";

import VueApexCharts from "vue-apexcharts";
import axios from "axios";

const profileTypeMap = {
  master_admin: 99,
  admin: 2,
  teacher: 4,
  monitor: 5,
};

const UserType = { ADMIN: 2, MASTER_ADMIN: 99 };

function normalizeUserPayload(payload) {
  let data = payload || {};

  for (let index = 0; index < 3; index += 1) {
    const nested = data && data.data;
    const nestedHasUserShape = nested && (
      nested.type ||
      nested.current_profile ||
      nested.profiles ||
      nested.profile_types ||
      nested.profile ||
      nested.role
    );

    if (nestedHasUserShape || (data && !data.type && !data.current_profile && !data.profiles && data.data)) {
      data = data.data;
    }
  }

  return data || {};
}

export default {
  name: "Home",

  components: {
    apexchart: VueApexCharts,
  },

  data: () => ({
    monitorQueueCount: 0,
    dataset: {
      loading: true,
      items: [],
      datePickerStartDialog: false,
      datePickerEndDialog: false,
      periodTypes: [
        { label: "Hoje", type: 1 },
        { label: "Ontem", type: 2 },
        { label: "7 dias", type: 3 },
        { label: "15 dias", type: 4 },
        { label: "30 dias", type: 5 },
        { label: "Esse mês", type: 6 },
        { label: "Mês passado", type: 7 },
        { label: "Últimos 3 meses", type: 8 },
        { label: "Personalizado", type: 9 },
      ],
      filterSelected: 3,
      rangeDate: { startDate: null, endDate: null },
      accessChart: {
        chartOptions: {
          chart: { toolbar: { show: false } },
          colors: ["#0554b5"],
          dataLabels: { enabled: false },
          grid: { borderColor: "#eef2f7" },
          plotOptions: { bar: { borderRadius: 5, columnWidth: "42%" } },
          xaxis: { categories: [] },
          yaxis: { title: { text: "Entradas" } },
        },
        series: [{ name: "Entradas", data: [] }],
      },
    },
  }),

  computed: {
    currentUser() {
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        return normalizeUserPayload(stored);
      } catch (error) {
        return {};
      }
    },

    currentUserTypes() {
      const types = [
        this.currentUser.type,
        this.currentUser.current_profile && this.currentUser.current_profile.type,
        ...((this.currentUser.profiles || []).map((profile) => profile.type)),
        ...((this.currentUser.profile_types || []).map((profileType) => profileTypeMap[profileType])),
        this.currentUser.profile ? UserType.ADMIN : null,
        this.currentUser.role ? UserType.ADMIN : null,
      ]
        .map((type) => Number(type))
        .filter(Boolean);

      return [...new Set(types)];
    },

    currentUserType() {
      if (this.currentUserTypes.includes(2)) return 2;
      if (this.currentUserTypes.includes(99)) return 99;
      return this.currentUserTypes[0] || null;
    },

    isAdministrativeProfile() {
      return this.currentUserTypes.includes(2);
    },

    profileHomeTitle() {
      if (this.currentUserType === 4) return "Painel do professor";
      if (this.currentUserType === 5) return "Painel da portaria";
      if (this.currentUserType === 99) return "Painel master";
      return "Painel operacional";
    },

    profileHomeSubtitle() {
      if (this.currentUserType === 4) return "Acesso rápido às rotinas pedagógicas do dia.";
      if (this.currentUserType === 5) return "Acesso rápido às rotinas de entrada, saída e acompanhamento.";
      if (this.currentUserType === 99) return "Gestão interna da plataforma SmartPin.";
      return "Acesso rápido às rotinas disponíveis para o seu perfil.";
    },

    profileActions() {
      if (this.currentUserType === 4) {
        return [
          { title: "Alunos", description: "Consultar alunos e vínculos de turma.", icon: "mdi-account-school-outline", route: "/alunos" },
          { title: "Avaliações", description: "Criar e acompanhar avaliações.", icon: "mdi-clipboard-text-outline", route: "/avaliacoes" },
          { title: "Diário de Classe", description: "Registrar presença e falta por aula.", icon: "mdi-calendar-check-outline", route: "/diario-classe" },
        ];
      }

      if (this.currentUserType === 5) {
        return [
          { title: "Painel de saída", description: "Chamar e liberar alunos aguardando saída.", icon: "mdi-exit-run", route: "/painel-saida", badge: this.monitorQueueCount },
          { title: "Alunos", description: "Consultar situação e presença dos alunos.", icon: "mdi-account-school-outline", route: "/alunos" },
          { title: "Diário de Classe", description: "Acompanhar registros de presença.", icon: "mdi-calendar-check-outline", route: "/diario-classe" },
          { title: "Ocorrências", description: "Registrar e acompanhar ocorrências.", icon: "mdi-account-eye-outline", route: "/ocorrencias" },
        ];
      }

      if (this.currentUserType === 99) {
        return [
          { title: "Instituições", description: "Criar e gerenciar tenants da plataforma.", icon: "mdi-domain", route: "/tenants" },
        ];
      }

      return [
        { title: "Alunos", description: "Consultar alunos.", icon: "mdi-account-school-outline", route: "/alunos" },
      ];
    },

    formattedRange() {
      const start = this.dataset.rangeDate.startDate;
      const end = this.dataset.rangeDate.endDate;
      if (!start || !end) return "";
      return `${this.$moment(start).format("DD/MM/YYYY")} - ${this.$moment(end).format("DD/MM/YYYY")}`;
    },

    totalAccesses() {
      return this.dataset.accessChart.series[0].data.reduce((sum, value) => sum + Number(value || 0), 0);
    },

    studentsInside() {
      return this.getCount("Alunos na escola");
    },

    studentsOutside() {
      return Math.max(this.getCount("Alunos") - this.studentsInside, 0);
    },

    equipmentOnline() {
      return this.getCount("Equipamentos online");
    },

    equipmentOffline() {
      return Math.max(this.getCount("Equipamentos") - this.equipmentOnline, 0);
    },

    presenceRate() {
      const total = this.getCount("Alunos");
      return total ? Math.round((this.studentsInside / total) * 100) : 0;
    },

    equipmentOnlineRate() {
      const total = this.getCount("Equipamentos");
      return total ? Math.round((this.equipmentOnline / total) * 100) : 0;
    },

    kpiCards() {
      return [
        { key: "students", title: "Alunos", subtitle: "cadastrados", value: this.getCount("Alunos"), icon: "mdi-account-school-outline" },
        { key: "inside", title: "Na escola", subtitle: "presença agora", value: this.studentsInside, icon: "mdi-school-outline" },
        { key: "accesses", title: "Acessos", subtitle: "no período", value: this.totalAccesses, icon: "mdi-door-open" },
        { key: "equipment", title: "Equipamentos online", subtitle: "com comunicação recente", value: this.equipmentOnline, icon: "mdi-router-network-wireless" },
        { key: "employees", title: "Colaboradores", subtitle: "cadastrados", value: this.getCount("Colaboradores"), icon: "mdi-account-multiple-outline" },
      ];
    },
  },

  mounted() {
    if (this.isAdministrativeProfile) this.loadData();
    if (this.currentUserType === 5) this.loadMonitorQueue();
  },

  methods: {
    async loadMonitorQueue() {
      try {
        const result = await axios.get("/api/v1/school-exit/requests", { params: { status: "requested,calling,released", limit: 100 } });
        this.monitorQueueCount = (result.data.content.data || []).length;
      } catch (error) {
        this.monitorQueueCount = 0;
      }
    },
    async loadData() {
      this.normalizeRangeByType();
      this.dataset.loading = true;

      try {
        await Promise.all([
          this.loadReportCount(),
          this.loadAccessDashboard(),
        ]);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.dataset.loading = false;
      }
    },

    async loadReportCount() {
      const result = await axios.get("/api/v1/dashboards/count-dashboard");
      this.dataset.items = result.data.content.items || [];
    },

    async loadAccessDashboard() {
      const { startDate, endDate } = this.dataset.rangeDate;
      const result = await axios.get(`/api/v1/dashboards/access-dashboard?start=${startDate}&end=${endDate}`);
      const items = (result.data.content.items || [])
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      this.dataset.accessChart = {
        ...this.dataset.accessChart,
        chartOptions: {
          ...this.dataset.accessChart.chartOptions,
          xaxis: {
            categories: items.map((item) => this.$moment(item.created_at).format("DD/MM")),
          },
        },
        series: [{ name: "Entradas", data: items.map((item) => Number(item.total || 0)) }],
      };
    },

    getCount(title) {
      const item = this.dataset.items.find((entry) => entry.title === title);
      return Number(item && item.value ? item.value : 0);
    },

    normalizeRangeByType() {
      switch (this.dataset.filterSelected) {
        case 1:
          this.dataset.rangeDate.startDate = this.$moment().format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().format("YYYY-MM-DD");
          break;
        case 2:
          this.dataset.rangeDate.startDate = this.$moment().subtract(1, "d").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().subtract(1, "d").format("YYYY-MM-DD");
          break;
        case 3:
          this.dataset.rangeDate.startDate = this.$moment().subtract(7, "d").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().format("YYYY-MM-DD");
          break;
        case 4:
          this.dataset.rangeDate.startDate = this.$moment().subtract(15, "d").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().format("YYYY-MM-DD");
          break;
        case 5:
          this.dataset.rangeDate.startDate = this.$moment().subtract(30, "d").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().format("YYYY-MM-DD");
          break;
        case 6:
          this.dataset.rangeDate.startDate = this.$moment().startOf("month").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().endOf("month").format("YYYY-MM-DD");
          break;
        case 7:
          this.dataset.rangeDate.startDate = this.$moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
          break;
        case 8:
          this.dataset.rangeDate.startDate = this.$moment().subtract(3, "month").format("YYYY-MM-DD");
          this.dataset.rangeDate.endDate = this.$moment().format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-page {
  padding-bottom: 32px !important;
}

.dashboard-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  margin: 4px 0 0;
}

.dashboard-range {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  color: #475569;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  min-height: 38px;
  padding: 0 14px;
}

.dashboard-filter {
  margin-bottom: 18px;
}

.dashboard-kpis {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-bottom: 18px;
}

.profile-actions {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.profile-action-card {
  align-items: center;
  background: #ffffff !important;
  border: 1px solid #e7edf5 !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.035) !important;
  color: #0f172a !important;
  display: flex;
  gap: 14px;
  min-height: 124px;
  padding: 20px;
  text-decoration: none;
  position: relative;
}

.profile-action-badge { position: absolute; right: 14px; top: 14px; }

.profile-action-card strong,
.profile-action-card span {
  display: block;
}

.profile-action-card strong {
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 4px;
}

.profile-action-card span {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.dashboard-kpi,
.dashboard-panel {
  background: #ffffff !important;
  border: 1px solid #e7edf5 !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.035) !important;
}

.dashboard-kpi {
  align-items: center;
  display: flex;
  gap: 12px;
  min-height: 112px;
  padding: 18px;
}

.dashboard-kpi-skeleton {
  overflow: hidden;
}

.kpi-skeleton-icon {
  flex: 0 0 42px;
}

.kpi-skeleton-icon ::v-deep .v-skeleton-loader__avatar {
  border-radius: 8px;
  height: 42px;
  width: 42px;
}

.kpi-skeleton-content {
  flex: 1;
}

.chart-skeleton {
  height: 300px;
}

.chart-skeleton ::v-deep .v-skeleton-loader__image {
  border-radius: 8px;
  height: 300px;
}

.presence-skeleton {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-top: 12px;
}

.kpi-icon {
  align-items: center;
  background: #eaf2ff;
  border-radius: 8px;
  color: var(--v-primary-base);
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.kpi-label,
.dashboard-kpi small {
  color: #64748b;
  display: block;
  font-size: 12px;
  font-weight: 700;
}

.dashboard-kpi strong {
  color: #0f172a;
  display: block;
  font-size: 28px;
  font-weight: 800;
  line-height: 34px;
}

.dashboard-panel {
  min-height: 100%;
  padding: 18px;
}

.panel-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.panel-header h2 {
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
  line-height: 24px;
  margin: 0;
}

.panel-header p {
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  margin: 2px 0 0;
}

.presence-panel {
  display: flex;
  flex-direction: column;
}

.presence-number {
  color: #0554b5;
  font-size: 58px;
  font-weight: 850;
  letter-spacing: 0;
  line-height: 68px;
  margin-top: 12px;
}

.presence-caption {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 28px;
}

.presence-bars,
.compact-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.presence-bars > div,
.status-row {
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 13px;
  font-weight: 650;
  justify-content: space-between;
}

.presence-bars strong,
.status-row strong {
  color: #0f172a;
  font-weight: 850;
}

.attention-item {
  align-items: flex-start;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  color: #9a3412;
  display: flex;
  gap: 10px;
  padding: 12px;
}

.attention-item.good {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.attention-item span {
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

@media only screen and (max-width: 1260px) {
  .dashboard-kpis,
  .profile-actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 760px) {
  .dashboard-header {
    flex-direction: column;
  }

  .dashboard-kpis,
  .profile-actions {
    grid-template-columns: 1fr;
  }
}
</style>
